/**
 * Reading and writing single string parameters in an invocation block's
 * `parameters` record, for the forms written for particular actions.
 *
 * The record is shared with the generic parameter list, so each write leaves
 * every other key as it was; a value that is empty, or equal to the action's
 * declared default, is removed rather than written, so the default applies.
 */

import { get, useFormContext, useFormState, useWatch } from "react-hook-form";

import { type ActionRecord } from "@/shared/domain/actions";

/** The action's declared default for `key`, as the string it would be sent as. */
export function declaredDefault(
  action: ActionRecord,
  key: string,
  fallback?: string,
): string | undefined {
  const declared = (action.parameters ?? []).find(
    (parameter) => parameter.name === key,
  )?.default;
  if (declared === undefined || declared === null) {
    return fallback;
  }
  return typeof declared === "string" ? declared : JSON.stringify(declared);
}

/** A string value as the form holds it: absent when the default applies. */
export type ParameterWrite = string | undefined;

/** `values` with each of `patch` applied; an undefined entry removes its key. */
export function withParameters(
  values: Readonly<Record<string, unknown>>,
  patch: Readonly<Record<string, ParameterWrite>>,
): Record<string, unknown> {
  const next: Record<string, unknown> = { ...values };
  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined || value.trim() === "") {
      delete next[key];
    } else {
      next[key] = value;
    }
  }
  return next;
}

/** A stored value as text; a non-string value is shown as its JSON. */
function asText(value: unknown): string | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }
  return typeof value === "string" ? value : JSON.stringify(value);
}

/**
 * The parameters record at `name`, a writer that patches it whole, so one
 * choice that sets a key and clears another is one write, and each key's
 * validation message. A write clears the messages of the keys it touches;
 * the next save checks them again.
 */
export function useParameterRecord(name: string) {
  const { control, getValues, setValue, clearErrors } = useFormContext();
  const values = (useWatch({ control, name }) ?? {}) as Record<string, unknown>;
  const { errors } = useFormState({ control, name });
  return {
    values,
    get: (key: string): string | undefined => asText(values[key]),
    error: (key: string): string | undefined =>
      (get(errors, `${name}.${key}`) as { message?: string } | undefined)
        ?.message,
    write: (patch: Readonly<Record<string, ParameterWrite>>) => {
      const current = (getValues(name) ?? {}) as Record<string, unknown>;
      setValue(name, withParameters(current, patch), {
        shouldDirty: true,
        shouldValidate: false,
      });
      clearErrors(Object.keys(patch).map((key) => `${name}.${key}`));
    },
  };
}

/** `value`, unless it is the default, in which case nothing is written. */
export function unlessDefault(
  value: string,
  defaultValue: string | undefined,
): ParameterWrite {
  return value === defaultValue ? undefined : value;
}

export interface SegmentedOption {
  /** What is written. */
  value: string;
  label: string;
  /** Stored spellings that mean this option, e.g. `s` for `seconds`. */
  aliases?: readonly string[];
}

/** The option a stored value means, matching aliases and ignoring case. */
export function optionFor(
  options: readonly SegmentedOption[],
  stored: string | undefined,
): SegmentedOption | undefined {
  if (stored === undefined) {
    return undefined;
  }
  const wanted = stored.trim().toLowerCase();
  return options.find(
    (option) =>
      option.value.toLowerCase() === wanted ||
      (option.aliases ?? []).some((alias) => alias.toLowerCase() === wanted),
  );
}

/** A comma-separated list as chips; blanks dropped. */
export function splitList(value: string | undefined): string[] {
  return (value ?? "")
    .split(",")
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}
